import { UHttp } from "../../utils/UHttp";

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

// Types pour les codes promo
export type DiscountType = 'percentage' | 'fixed_amount';

export interface PromoCodeUsage {
  clientId: string;
  bookingId: string;
  usedAt: Date;
}

export interface PromoCode {
  id: string;
  companyId: string;
  code: string;
  discountType: DiscountType;
  discountValue: number;
  validFrom: Date;
  validUntil: Date | null;
  isActive: boolean;
  assignedClientIds: string[] | null;
  usedByClients: PromoCodeUsage[];
  applicableServiceIds: string[] | null;
  stripeCouponId: string | null;
  emailsSentAt: Date | null;
  emailsSentCount: number;
  description: string | null;
  internalNotes: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// Types pour les réponses API
export interface PromoCodeResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  status?: number;
}

export interface PromoCodesResponse {
  success: boolean;
  message?: string;
  data: PromoCode[];
}

export interface CanCreatePromoCodeResponse {
  success: boolean;
  data: {
    canCreate: boolean;
    message?: string;
  };
}

export interface CreatePromoCodeData {
  code: string;
  discountType: DiscountType;
  discountValue: number;
  validFrom: Date;
  validUntil?: Date | null;
  description?: string;
  internalNotes?: string;
  assignedClientIds?: string[] | null;
  applicableServiceIds?: string[] | null;
}

export interface UpdatePromoCodeData extends Partial<CreatePromoCodeData> {
  isActive?: boolean;
}

export interface PromoCodeStats {
  totalUsages: number;
  uniqueClients: number;
  totalDiscountGiven: number;
}

export class PromoCodeModel {
  private static instance: PromoCodeModel;

  private constructor() {}

  public static getInstance(): PromoCodeModel {
    if (!PromoCodeModel.instance) {
      PromoCodeModel.instance = new PromoCodeModel();
    }
    return PromoCodeModel.instance;
  }

  /**
   * Récupère tous les codes promo d'une entreprise
   */
  public async getPromoCodesByCompany(companyId: string): Promise<PromoCodesResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/promo-code/company/${companyId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      throw error;
    }
  }

  /**
   * Récupère les codes promo actifs d'une entreprise
   */
  public async getActivePromoCodesByCompany(companyId: string): Promise<PromoCodesResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/promo-code/company/${companyId}/active`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching active promo codes:', error);
      throw error;
    }
  }

  /**
   * Vérifie si l'owner peut créer un code promo
   */
  public async canCreatePromoCode(companyId: string): Promise<CanCreatePromoCodeResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/promo-code/company/${companyId}/can-create`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking if can create promo code:', error);
      throw error;
    }
  }

  /**
   * Récupère un code promo par ID
   */
  public async getPromoCodeById(id: string): Promise<PromoCodeResponse<PromoCode>> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/promo-code/${id}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching promo code:', error);
      throw error;
    }
  }

  /**
   * Crée un code promo
   */
  public async createPromoCode(companyId: string, data: CreatePromoCodeData): Promise<PromoCodeResponse<PromoCode>> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/promo-code/company/${companyId}`,
        data,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return { ...responseData, status: response.status };
    } catch (error) {
      console.error('Error creating promo code:', error);
      throw error;
    }
  }

  /**
   * Met à jour un code promo
   */
  public async updatePromoCode(id: string, data: UpdatePromoCodeData): Promise<PromoCodeResponse<PromoCode>> {
    try {
      const response = await UHttp.put(
        `${apiUrlServer}/api/promo-code/${id}`,
        data,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return { ...responseData, status: response.status };
    } catch (error) {
      console.error('Error updating promo code:', error);
      throw error;
    }
  }

  /**
   * Active ou désactive un code promo
   */
  public async togglePromoCode(id: string, isActive: boolean): Promise<PromoCodeResponse<PromoCode>> {
    try {
      const response = await UHttp.patch(
        `${apiUrlServer}/api/promo-code/${id}/toggle`,
        { isActive },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return { ...responseData, status: response.status };
    } catch (error) {
      console.error('Error toggling promo code:', error);
      throw error;
    }
  }

  /**
   * Supprime (soft delete) un code promo
   */
  public async deletePromoCode(id: string): Promise<PromoCodeResponse> {
    try {
      const response = await UHttp.delete(
        `${apiUrlServer}/api/promo-code/${id}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { ...data, status: response.status };
    } catch (error) {
      console.error('Error deleting promo code:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques d'un code promo
   */
  public async getPromoCodeStats(id: string): Promise<PromoCodeResponse<PromoCodeStats>> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/promo-code/${id}/stats`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching promo code stats:', error);
      throw error;
    }
  }

  /**
   * Valide un code promo pour un client
   */
  public async validatePromoCode(data: {
    code: string;
    clientId: string;
    companyId: string;
    amount: number;
  }): Promise<PromoCodeResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/promo-code/validate`,
        data,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error validating promo code:', error);
      throw error;
    }
  }

  /**
   * Envoie un code promo par email aux clients
   */
  public async sendPromoCodeEmail(
    promoCodeId: string,
    clientIds: string[]
  ): Promise<PromoCodeResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/promo-code/${promoCodeId}/send-email`,
        { clientIds },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error sending promo code email:', error);
      throw error;
    }
  }
}

export default PromoCodeModel.getInstance();
